import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';
import './BuyerDomesticHvi.css';

const MultiSelectDropdown = ({ title, options, selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const handleToggle = (val) => {
    const next = selectedValues.includes(val)
      ? selectedValues.filter(v => v !== val)
      : [...selectedValues, val];
    onChange(next);
  };

  const filteredOptions = options.filter(opt => 
    String(opt).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="multi-select-dropdown-container">
      <button className="multi-select-trigger-btn" onClick={() => { setIsOpen(!isOpen); setSearchTerm(''); }}>
        <span className="trigger-text">
          {selectedValues.length === 0 ? title : `${title} (${selectedValues.length})`}
        </span>
        <span className="trigger-arrow">▼</span>
      </button>
      {isOpen && (
        <>
          <div className="dropdown-overlay-click-close" onClick={() => setIsOpen(false)} />
          <div className="dropdown-options-list animate-scale-up">
            <div className="dropdown-search-box">
              <input 
                type="text" 
                placeholder={`Type to search...`} 
                value={searchTerm} 
                onChange={e => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
            {filteredOptions.length > 0 ? filteredOptions.map(opt => (
              <div key={opt} className="dropdown-option-item" onClick={() => handleToggle(opt)} style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt)}
                  readOnly
                />
                <span>{opt}</span>
              </div>
            )) : (
              <div className="dropdown-no-results">No matches found</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const BuyerDomesticHvi = ({ viewMode, setViewMode, currentProduct: propProduct, currentSubType: propSubType }) => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('current_bid');
  const currentSubType = propSubType || searchParams.get('subType') || 'hvi';
  const currentProduct = propProduct || searchParams.get('product') || 'cotton';

  const [sellerListings, setSellerListings] = useState(() => {
    const saved = localStorage.getItem('sellerEntries');
    return saved ? JSON.parse(saved) : [];
  });

  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const handleStorage = () => {
      const saved = localStorage.getItem('sellerEntries');
      if (saved) setSellerListings(JSON.parse(saved));
    };
    window.addEventListener('storage', handleStorage);

    const timer = setInterval(() => setNow(new Date()), 1000);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(timer);
    };
  }, []);

  const calculateTimeLeftForListing = (listing) => {
    if (!listing) return 'No Limit';
    const targetDate = listing.sellingType === 'forward' ? listing.endDate : listing.validDate;
    const closingTime = listing.closingTime || '12:00';
    if (!targetDate) return 'No Limit';
    const expiry = new Date(`${targetDate}T${closingTime}`);
    const diff = expiry - now;

    if (diff <= 0) return 'EXPIRED';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const [biddingData, setBiddingData] = useState({
    quantity: '',
    bidDiff: '',
    customBidPrice: '',
    certifications: { bci: false, cmia: false, regenAgri: false },
    paymentDueDate: ''
  });

  const [showBidModal, setShowBidModal] = useState(false);
  const [currentListing, setCurrentListing] = useState(null);
  const [activeRemarks, setActiveRemarks] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'offerPrice', direction: 'high-to-low' });
  const [ignoreCerts, setIgnoreCerts] = useState(true);  // Default to ignored, buyer can unlock
  const [activeHeaderDropdown, setActiveHeaderDropdown] = useState(null);

  const [rangeFilters, setRangeFilters] = useState({});
  const [textFilters, setTextFilters] = useState({});

  // Reset filters on subtype or product change
  useEffect(() => {
    setRangeFilters({});
    setTextFilters({});
  }, [currentSubType, currentProduct]);

  const openBidModal = (listing) => {
    setCurrentListing(listing);
    setIgnoreCerts(false);
    const certBids = {};
    if (listing.selectedCerts && listing.selectedCerts.length > 0) {
      listing.selectedCerts.forEach(c => {
        certBids[c.id] = {
          checked: true,
          bidPremium: c.premium || 0
        };
      });
    }
    setBiddingData({
      quantity: '',
      bidDiff: '',
      customBidPrice: '',
      certifications: { bci: false, cmia: false, regenAgri: false },
      certBids,
      securityPin: '',
      paymentDueDate: listing.paymentDueDate || ''
    });
    setShowBidModal(true);
  };

  const submitBid = async () => {
    if (!biddingData.quantity) {
      alert('Please enter a quantity');
      return;
    }

    const offerPrice = parseFloat(biddingData._forwardOfferPrice || currentListing.offerPrice || 0);
    let finalBidPrice = 0;

    if (!biddingData.customBidPrice) {
      alert('Please enter your custom bid price');
      return;
    }
    finalBidPrice = parseFloat(biddingData.customBidPrice);

    if (finalBidPrice % 100 !== 0) {
      alert('Bid price for the product must be a multiple of 100 (e.g. 55000, 55100).');
      return;
    }

    // Process separate certificate bids
    const includedCerts = [];
    if (!ignoreCerts) {
      const certKeys = Object.keys(biddingData.certBids || {});
      for (const cId of certKeys) {
        const cb = biddingData.certBids[cId];
        if (cb.checked) {
          const bidPremiumVal = cb.bidPremium === '' ? 0 : parseFloat(cb.bidPremium);
          if (bidPremiumVal % 100 !== 0) {
            alert(`Bid premium for ${cId} must be a multiple of 100.`);
            return;
          }
          const certObj = (currentListing.selectedCerts || []).find(c => c.id === cId);
          includedCerts.push({
            id: cId,
            name: certObj ? certObj.name : cId,
            sellerPremium: certObj ? certObj.premium : 0,
            buyerBidPremium: bidPremiumVal
          });
        }
      }
    }

    if (!biddingData.securityPin) {
      alert('Please enter your 4-digit Security PIN to confirm the bid.');
      return;
    }

    let extraPaymentCharge = 0;
    if (biddingData.paymentDueDate && currentListing.paymentDueDate) {
      const diffTime = new Date(biddingData.paymentDueDate) - new Date(currentListing.paymentDueDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const maxDelay = parseInt(currentListing.paymentDelayMax || 0);
      
      if (diffDays > maxDelay) {
        alert(`Your selected payment date exceeds the seller's maximum delay limit of ${maxDelay} days.`);
        return;
      }
      
      if (diffDays > 0) {
        extraPaymentCharge = diffDays * parseFloat(currentListing.latePaymentCharge || 0);
      }
    }

    try {
      const token = localStorage.getItem('token');
      const pinResponse = await api.post(`/user/verify-pin`, {
        pin: biddingData.securityPin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!pinResponse.data.success) {
        alert('Invalid Security PIN');
        return;
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to verify Security PIN');
      return;
    }

    const newBid = {
      id: Date.now(),
      listingId: currentListing.id,
      buyerName: localStorage.getItem('userName') || 'Prime Buyer',
      quantity: biddingData.quantity,
      bidPrice: finalBidPrice,
      bidDiffSelected: biddingData.bidDiff,
      certifications: biddingData.certifications,
      includedCerts,
      paymentDueDate: biddingData.paymentDueDate,
      latePaymentExtraCharge: extraPaymentCharge,
      timestamp: new Date().toISOString(),
      status: 'Pending',
      seen: false
    };
    const allBids = JSON.parse(localStorage.getItem('bids') || '[]');
    localStorage.setItem('bids', JSON.stringify([...allBids, newBid]));

    // Dispatch custom event for immediate same-window updates
    window.dispatchEvent(new Event('bidsUpdated'));

    alert('Bid submitted successfully! It is now visible to the seller.');
    setShowBidModal(false);
  };

  const handleBuyerRespondToCounter = async (bidId, action) => {
    const pin = prompt(`To ${action.toLowerCase()} this counter offer, please enter your 4-digit Security PIN:`);
    if (pin === null) return; // user cancelled prompt
    if (!pin) {
      alert('Security PIN is required.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const pinResponse = await api.post(`/user/verify-pin`, { pin }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!pinResponse.data.success) {
        alert('Invalid Security PIN');
        return;
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to verify Security PIN');
      return;
    }

    const allBids = JSON.parse(localStorage.getItem('bids') || '[]');
    const updatedBids = allBids.map(bid => {
      if (bid.id === bidId) {
        const updated = { ...bid, status: action };
        if (action === 'Accepted') {
          updated.bidPrice = bid.counterProductPrice;
          if (bid.counterCertPrices) {
            updated.includedCerts = (bid.includedCerts || []).map(c => ({
              ...c,
              buyerBidPremium: bid.counterCertPrices[c.id] !== undefined ? bid.counterCertPrices[c.id] : c.buyerBidPremium
            }));
          }
        }
        return updated;
      }
      return bid;
    });
    localStorage.setItem('bids', JSON.stringify(updatedBids));
    const userName = localStorage.getItem('userName') || 'Prime Buyer';
    setMyBids(updatedBids.filter(b => b.buyerName === userName));
    window.dispatchEvent(new Event('bidsUpdated'));
    alert(`Counter offer ${action.toLowerCase()} successfully!`);
  };

  const toggleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'high-to-low' ? 'low-to-high' : 'high-to-low'
    }));
  };

  const filteredListings = sellerListings.filter(l => {
    const isSubMatch = l.subType === currentSubType || (!l.subType && currentSubType === 'hvi');
    const isProdMatch = l.productType === currentProduct || (!l.productType && currentProduct === 'cotton');
    if (!isSubMatch || !isProdMatch) return false;
    
    if (activeTab === 'current_bid') {
      return l.sellingType !== 'forward';
    } else if (activeTab === 'forward_bidding') {
      return l.sellingType === 'forward';
    }
    return true;
  });

  const getHeaders = () => {
    let headers = [];
    switch (currentSubType) {
      case 'hvi':
        headers = [
          { label: 'Crop Year', key: 'cropYear' }, { label: 'State', key: 'state' }, { label: 'Station', key: 'station' },
          { label: 'Gin Lot Num/Bale No', key: 'lotNumber' }, { label: 'Warehouse Location', key: 'warehouse' },
          { label: 'Lab Name', key: 'labName' }, { label: 'SCI', key: 'sci' }, { label: 'Mic', key: 'mic' },
          { label: 'SL(mm)', key: 'sl' }, { label: 'UR(%)', key: 'ur' }, { label: 'SF(%)', key: 'sf' },
          { label: 'Str(g/tex)', key: 'str' }, { label: 'Elg(%)', key: 'elg' }, { label: 'Rd', key: 'rd' },
          { label: '+b', key: 'plusB' }, { label: 'CGrd', key: 'cgrd' }, { label: 'Time Left', key: 'timeLeft' },
          { label: 'Offer Price', key: 'offerPrice' },
          { label: 'Cert', key: 'certification' },
          { label: 'Premium', key: 'totalPremium' }, { label: 'Remarks', key: 'remarks' }, { label: 'Bids', key: 'actions' }
        ];
        break;
      case 'domestic-desc':
        headers = [
          { label: 'Crop Year', key: 'cropYear' }, { label: 'State', key: 'state' }, { label: 'Station/Zone', key: 'station' },
          { label: 'Mic Range', key: 'micRange' }, { label: 'SL(mm)', key: 'slRange' }, { label: 'Moisture(%)', key: 'moistureRange' },
          { label: 'Trash(%)', key: 'trashRange' }, { label: 'Str(g/tex)', key: 'strRange' }, { label: 'Rd', key: 'rdRange' },
          { label: 'Offer Qty(bales)', key: 'offerQty' }, { label: 'Time Left', key: 'timeLeft' }, { label: 'Offer Price', key: 'offerPrice' },
          { label: 'Cert', key: 'certification' },
          { label: 'Premium', key: 'totalPremium' }, { label: 'Remarks', key: 'remarks' }, { label: 'Bids', key: 'actions' }
        ];
        break;
      case 'import-desc':
        headers = [
          { label: 'Crop Year', key: 'cropYear' }, { label: 'Growth', key: 'growth' },
          { label: 'MT', key: 'offerQty' },
          { label: 'Quality', key: 'quality' }, { label: 'Shipment', key: 'shipment' },
          { label: 'Time Left', key: 'timeLeft' }, { label: 'Offer Price', key: 'offerPrice' }, { label: 'Cert', key: 'certification' },
          { label: 'Premium', key: 'totalPremium' }, { label: 'Remarks', key: 'remarks' }, { label: 'Bids', key: 'actions' }
        ];
        break;
      case 'import-recap':
        headers = [
          { label: 'Crop Year', key: 'cropYear' }, { label: 'Growth', key: 'growth' },
          { label: 'MT', key: 'offerQty' },
          { label: 'Quality', key: 'quality' }, { label: 'Ref/Recap File', key: 'recapFileName' }, { label: 'Shipment', key: 'shipment' },
          { label: 'Time Left', key: 'timeLeft' }, { label: 'Offer Price', key: 'offerPrice' }, { label: 'Cert', key: 'certification' },
          { label: 'Premium', key: 'totalPremium' }, { label: 'Remarks', key: 'remarks' }, { label: 'Bids', key: 'actions' }
        ];
        break;
      case 'waste':
        headers = [
          { label: 'Type of Waste', key: 'wasteType' }, { label: 'Crop Year', key: 'cropYear' },
          { label: 'Origin/State', key: 'state' },
          { label: 'SL(mm)', key: 'sl' }, { label: 'Moisture(%)', key: 'moisture' },
          { label: 'Blend with MMF(Y/N)', key: 'blendWithMMF' }, { label: 'Contamination Free(Y/N)', key: 'contaminationFree' },
          { label: 'Cloth covered Bales(Y/N)', key: 'clothCovered' }, { label: 'Offer Qty(bales)', key: 'offerQty' },
          { label: 'Offer Price/kg', key: 'offerPrice' }, { label: 'Time Left', key: 'timeLeft' },
          { label: 'Remarks', key: 'remarks' }, { label: 'Bids', key: 'actions' }
        ];
        break;
      default:
        headers = [
          { label: 'Crop Year', key: 'cropYear' },
          { label: 'Offer Qty', key: 'offerQty' },
          { label: 'Offer Price', key: 'offerPrice' }, { label: 'Time Left', key: 'timeLeft' },
          { label: 'Cert', key: 'certification' },
          { label: 'Premium', key: 'totalPremium' }, { label: 'Remarks', key: 'remarks' }, { label: 'Bids', key: 'actions' }
        ];
        break;
    }

    const customTitles = [];
    filteredListings.forEach(listing => {
      if (listing.customParams && Array.isArray(listing.customParams)) {
        listing.customParams.forEach(p => {
          if (p.title && !customTitles.includes(p.title)) {
            customTitles.push(p.title);
          }
        });
      }
    });

    if (customTitles.length > 0) {
      const customHeaders = customTitles.map(title => ({ label: title, key: `cp_${title}` }));
      headers.splice(1, 0, ...customHeaders);
    }

    // Inject Seller Name at index 1
    headers.splice(1, 0, { label: 'Seller Name', key: 'sellerName' });

    if (activeTab === 'forward_bidding') {
      const timeLeftIdx = headers.findIndex(h => h.key === 'timeLeft');
      if (timeLeftIdx !== -1) {
        headers.splice(timeLeftIdx, 0, 
          { label: 'Starting Date', key: 'startDate' },
          { label: 'Ending Date', key: 'endDate' }
        );
      } else {
        headers.push(
          { label: 'Starting Date', key: 'startDate' },
          { label: 'Ending Date', key: 'endDate' }
        );
      }
    }

    // Inject IncoTerms before Remarks or Bids
    const remarksIdx = headers.findIndex(h => h.key === 'remarks');
    if (remarksIdx !== -1) {
      headers.splice(remarksIdx, 0, { label: 'IncoTerms', key: 'transportService' });
    } else {
      headers.push({ label: 'IncoTerms', key: 'transportService' });
    }

    return headers;
  };

  const renderCell = (listing, key) => {
    if (key === 'actions') {
      const isExpired = calculateTimeLeftForListing(listing) === 'EXPIRED';
      return (
        <button
          className={`btn-bid-action-small ${isExpired ? 'expired-btn' : ''}`}
          onClick={() => !isExpired && openBidModal(listing)}
          disabled={isExpired}
        >
          {isExpired ? 'Bids Closed' : 'Place Bid'}
        </button>
      );
    }
    if (key === 'timeLeft') {
      const timeLeft = calculateTimeLeftForListing(listing);
      return <span className={`time-tag-mini ${timeLeft === 'EXPIRED' ? 'expired' : ''}`}>{timeLeft}</span>;
    }
    if (key === 'offerPrice') {
      if (listing.sellingType === 'forward' && listing.monthlyPrices) {
        const n = new Date();
        const MN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const key2 = `${MN[n.getMonth()]}-${n.getFullYear()}`;
        const prices = listing.monthlyPrices instanceof Map
          ? Object.fromEntries(listing.monthlyPrices)
          : (listing.monthlyPrices || {});
        const mp = prices[key2];
        if (mp) return `₹${parseInt(mp).toLocaleString()} (${key2})`;
        // Fallback: show nearest available month price
        const entries = Object.entries(prices).filter(([,v]) => v);
        if (entries.length) {
          const [mk, mv] = entries[0];
          return `₹${parseInt(mv).toLocaleString()} (${mk})`;
        }
      }
      return `₹${parseInt(listing.offerPrice || 0).toLocaleString()}`;
    }
    if (key === 'quality') {
      const cgPart = listing.colourGrade ? `CG:${listing.colourGrade}` : '';
      const leafPart = (listing.leafMin || listing.leafMax) ? `Leaf:${listing.leafMin || '-'}-${listing.leafMax || '-'}` : (listing.leaf ? `Leaf:${listing.leaf}` : '');
      const staplePart = (listing.stapleMin || listing.stapleMax) ? `Staple:${listing.stapleMin || '-'}-${listing.stapleMax || '-'}` : (listing.staple ? `Staple:${listing.staple}` : '');
      const micPart = (listing.micMin || listing.micMax) ? `Mic:${listing.micMin || '-'}-${listing.micMax || '-'}` : (listing.mic ? `Mic:${listing.mic}` : '');
      const gptPart = (listing.gptMin || listing.gptMax) ? `GPT:${listing.gptMin || '-'}-${listing.gptMax || '-'}` : (listing.gpt ? `GPT:${listing.gpt}` : '');
      const gptAvgPart = (listing.gptMinAvgMin || listing.gptMinAvgMax) ? `GPT Avg:${listing.gptMinAvgMin || '-'}-${listing.gptMinAvgMax || '-'}` : (listing.gptMinAvg ? `GPT Avg:${listing.gptMinAvg}` : '');
      return [cgPart, leafPart, staplePart, micPart, gptPart, gptAvgPart].filter(Boolean).join(' | ') || '-';
    }
    if (key === 'recapFileName') return listing.recapFileName ? <span className="file-link">📄 {listing.recapFileName}</span> : '-';
    if (key === 'totalPremium') return `₹${listing.totalPremium || 0}`;
    if (key === 'startDate') return listing.startDate || '-';
    if (key === 'endDate') return listing.endDate || '-';
    if (key === 'remarks') {
      if (!listing.remarks) return '-';
      return (
        <button
          className="btn-remarks-read"
          onClick={() => setActiveRemarks(listing.remarks)}
        >
          Read
        </button>
      );
    }
    
    // Formatting technical parameters (plain values without units & suffixes as per request)
    if (key === 'sl' && listing.sl) return listing.sl;
    if (key === 'mic' && listing.mic) return listing.mic;
    if (key === 'str' && listing.str) return listing.str;
    if (key === 'rd' && listing.rd) return listing.rd;
    if (key === 'plusB' && listing.plusB) return listing.plusB;
    if (key === 'ur' && listing.ur) return listing.ur;
    
    if (key === 'micRange') {
      if (listing.micMin || listing.micMax) return `${listing.micMin || '-'}-${listing.micMax || '-'}`;
      return listing.micRange || listing.mic || '-';
    }
    if (key === 'slRange') {
      if (listing.slMin || listing.slMax) return `${listing.slMin || '-'}-${listing.slMax || '-'}`;
      return listing.sl || '-';
    }
    if (key === 'moistureRange') {
      if (listing.moistureMin || listing.moistureMax) return `${listing.moistureMin || '-'}-${listing.moistureMax || '-'}`;
      return listing.moisture || '-';
    }
    if (key === 'trashRange') {
      if (listing.trashMin || listing.trashMax) return `${listing.trashMin || '-'}-${listing.trashMax || '-'}`;
      return listing.trash || '-';
    }
    if (key === 'strRange') {
      if (listing.strMin || listing.strMax) return `${listing.strMin || '-'}-${listing.strMax || '-'}`;
      return listing.str || '-';
    }
    if (key === 'rdRange') {
      if (listing.rdMin || listing.rdMax) return `${listing.rdMin || '-'}-${listing.rdMax || '-'}`;
      return listing.rd || '-';
    }
    
    // Handle dynamic custom param keys (prefixed with cp_)
    if (key.startsWith('cp_')) {
      const paramTitle = key.substring(3);
      if (listing.customParams && Array.isArray(listing.customParams)) {
        const found = listing.customParams.find(p => p.title === paramTitle);
        return found ? found.value : '-';
      }
      return '-';
    }
    if (key === 'offerQty') {
      if (!listing.offerQty) return '-';
      let unit = '';
      if (listing.offerQtyTerms) {
        // Support both old long labels and new short labels
        const terms = listing.offerQtyTerms;
        unit = (terms === 'Bales' || terms === 'Terms of BALS' || terms === 'BALS') ? ' Bales' : ' MT';
      } else {
        unit = listing.subType?.includes('import') ? ' MT' : ' Bales';
      }
      return `${listing.offerQty}${unit}`;
    }
    if (key === 'sellerName') return listing.sellerName || 'Seller A';
    if (key === 'transportService') {
      if (listing.transportService === 'FOR') {
        return `FOR - ${listing.forState || ''}`;
      }
      return listing.transportService || 'EX.Whr';
    }
    return listing[key] || '-';
  };

  // 1. Filter by dynamic range and text search
  const finalFilteredListings = filteredListings.filter(l => {
    // Range filters (numeric)
    for (const key of Object.keys(rangeFilters)) {
      const { min, max } = rangeFilters[key] || {};
      const minNum = parseFloat(min);
      const maxNum = parseFloat(max);
      
      if (!isNaN(minNum) || !isNaN(maxNum)) {
        let valStr = '';
        if (key.startsWith('cp_')) {
          const paramTitle = key.substring(3);
          if (l.customParams && Array.isArray(l.customParams)) {
            const found = l.customParams.find(p => p.title === paramTitle);
            if (found) valStr = found.value;
          }
        } else {
          valStr = l[key] || '';
        }
        
        const valNum = parseFloat(String(valStr).replace(/[^0-9.-]+/g, ""));
        if (isNaN(valNum)) return false;
        if (!isNaN(minNum) && valNum < minNum) return false;
        if (!isNaN(maxNum) && valNum > maxNum) return false;
      }
    }
    // Text search filters (string matches)
    for (const key of Object.keys(textFilters)) {
      const query = textFilters[key];
      if (query && query.trim() !== '') {
        if (key === 'state') {
          const stateMap = {
            'PB': 'Punjab', 'HR': 'Haryana', 'RJ': 'Rajasthan', 'GJ': 'Gujarat', 
            'MH': 'Maharashtra', 'MP': 'Madhya Pradesh', 'OD': 'Odisha', 
            'TG': 'Telangana', 'AP': 'Andhra Pradesh', 'KA': 'Karnataka', 
            'TN': 'Tamil Nadu', 'KL': 'Kerala'
          };
          const fullStateName = stateMap[query] || query;
          const itemState = l.state || (l.states && l.states.length ? l.states[0] : '');
          if (!itemState || !String(itemState).toLowerCase().includes(fullStateName.toLowerCase())) {
            return false;
          }
          continue;
        }

        let valStr = '';
        if (key === 'sellerName') {
          valStr = l.sellerName || 'Seller A';
        } else if (key.startsWith('cp_')) {
          const paramTitle = key.substring(3);
          if (l.customParams && Array.isArray(l.customParams)) {
            const found = l.customParams.find(p => p.title === paramTitle);
            if (found) valStr = found.value;
          }
        } else {
          valStr = l[key] || '';
        }
        if (!String(valStr).toLowerCase().includes(query.toLowerCase())) {
          return false;
        }
      }
    }
    return true;
  });

  // 2. Sort the filtered listings
  const sortedListings = [...finalFilteredListings].sort((a, b) => {
    let valA = a[sortConfig.key], valB = b[sortConfig.key];
    const numA = parseFloat(valA), numB = parseFloat(valB);
    if (!isNaN(numA) && !isNaN(numB)) { valA = numA; valB = numB; }
    else { valA = String(valA || '').toLowerCase(); valB = String(valB || '').toLowerCase(); }
    return sortConfig.direction === 'low-to-high' ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
  });

  const [myBids, setMyBids] = useState(() => {
    const allBids = JSON.parse(localStorage.getItem('bids') || '[]');
    const userName = localStorage.getItem('userName') || 'Prime Buyer';
    return allBids.filter(b => b.buyerName === userName);
  });

  useEffect(() => {
    const fetchBids = () => {
      const allBids = JSON.parse(localStorage.getItem('bids') || '[]');
      const userName = localStorage.getItem('userName') || 'Prime Buyer';
      setMyBids(allBids.filter(b => b.buyerName === userName));
    };
    window.addEventListener('storage', fetchBids);
    window.addEventListener('bidsUpdated', fetchBids);
    return () => {
      window.removeEventListener('storage', fetchBids);
      window.removeEventListener('bidsUpdated', fetchBids);
    };
  }, []);

  const allCurrentMarketListings = sellerListings.filter(l => 
    (l.subType === currentSubType || (!l.subType && currentSubType === 'hvi')) &&
    (l.productType === currentProduct || (!l.productType && currentProduct === 'cotton'))
  );

  const uniqueCropYears = [...new Set(allCurrentMarketListings.map(l => String(l.cropYear || '')).filter(Boolean))].sort();
  const uniqueStates = [...new Set(allCurrentMarketListings.map(l => String(l.state || l.origin || '')).filter(Boolean))].sort();
  const uniqueStations = [...new Set(allCurrentMarketListings.map(l => String(l.station || '')).filter(Boolean))].sort();
  const getColumnClass = (key) => {
    const primaryKeys = [
      'cropYear', 'state', 'station', 'sellerName', 'lotNumber', 'warehouse', 'labName',
      'origin', 'growth', 'wasteType', 'shipFrom', 'shipment', 'eta', 'offerQty', 'transportService'
    ];
    const pricingKeys = [
      'offerPrice', 'totalPremium', 'certification', 'cifIndiaBasis', 'uscLbs', 'inrCandy', 'remarks', 'actions', 'timeLeft'
    ];
    if (primaryKeys.includes(key)) return 'col-primary-info';
    if (pricingKeys.includes(key)) return 'col-pricing';
    return 'col-technical-param';
  };

  return (
    <div className="buyer-hvi-wrapper">
      <div className="buyer-view-tabs" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            className={`view-tab ${activeTab === 'current_bid' ? 'active' : ''}`}
            onClick={() => setActiveTab('current_bid')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            ☀️ Spot Bid
          </button>
          <button
            className={`view-tab ${activeTab === 'forward_bidding' ? 'active' : ''}`}
            onClick={() => setActiveTab('forward_bidding')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            🔮 Forward Bidding
          </button>
          <button
            className={`view-tab ${activeTab === 'mybids' ? 'active' : ''}`}
            onClick={() => setActiveTab('mybids')}
          >
            My Bids / Offers
          </button>
        </div>
      </div>

      {(activeTab === 'current_bid' || activeTab === 'forward_bidding') ? (
        <div className="marketplace-section animate-fade-in">
          <div className="buyer-header-controls-single-line" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div className="header-left-compact">
              <span className="sort-hint-compact" style={{ fontWeight: '700', fontSize: '0.8rem', color: '#475569' }}>
                {activeTab === 'forward_bidding' ? '🔮 Forward Bidding Terminal' : '☀️ Spot Bid Terminal'} &nbsp;·&nbsp; ({currentSubType.replace('-', ' ').toUpperCase()})
              </span>
            </div>
            <div className="header-right-compact">
              <div className="state-quick-filters" style={{ display: 'flex', gap: '4px', overflowX: 'auto' }}>
                {['All', 'PB', 'HR', 'RJ', 'GJ', 'MH', 'MP', 'OD', 'TG', 'AP', 'KA', 'TN', 'KL'].map(s => (
                  <button 
                    key={s}
                    onClick={() => setTextFilters(prev => ({ ...prev, state: s === 'All' ? '' : s }))}
                    style={{
                      padding: '2px 8px',
                      fontSize: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #cbd5e1',
                      background: textFilters.state === s || (s === 'All' && !textFilters.state) ? '#0d47a1' : 'white',
                      color: textFilters.state === s || (s === 'All' && !textFilters.state) ? 'white' : '#334155',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="table-responsive-detailed">
            <table className="premium-table-detailed">
              <thead>
                <tr>
                  {getHeaders().map(header => {
                    const isSortableAndFilterable = header.key !== 'actions' && header.key !== 'remarks' && header.key !== 'timeLeft' && header.key !== 'recapFileName';
                    const isNumeric = ['sci', 'mic', 'sl', 'ur', 'sf', 'str', 'elg', 'rd', 'plusB', 'offerQty', 'offerPrice', 'totalPremium', 'trash', 'moisture', 'cifIndiaBasis', 'uscLbs', 'inrCandy'].includes(header.key);
                    const isDropdownOpen = activeHeaderDropdown === header.key;

                    return (
                      <th 
                        key={header.key} 
                        className={`${sortConfig.key === header.key ? 'active-sort' : ''} ${getColumnClass(header.key)}`}
                        style={{ position: 'relative' }}
                      >
                        <div className="header-cell-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '4px' }} onClick={() => isSortableAndFilterable && toggleSort(header.key)}>
                          <span>{header.label}</span>
                          
                          {isSortableAndFilterable && (
                            <button
                              type="button"
                              className="header-dropdown-trigger"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveHeaderDropdown(isDropdownOpen ? null : header.key);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '2px 4px',
                                fontSize: '0.62rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: sortConfig.key === header.key ? '#0d47a1' : '#64748b',
                                transition: 'color 0.2s',
                                fontWeight: '900'
                              }}
                            >
                              ▼
                            </button>
                          )}
                        </div>

                        {/* Excel-like dropdown popup for this column */}
                        {isSortableAndFilterable && isDropdownOpen && (
                          <>
                            <div 
                              className="dropdown-overlay-click-close" 
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveHeaderDropdown(null);
                              }} 
                              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999, background: 'transparent' }}
                            />
                            <div 
                              className="excel-dropdown-popup animate-scale-up"
                              onClick={(e) => e.stopPropagation()} 
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                zIndex: 1000,
                                background: 'white',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                                padding: '8px',
                                width: '180px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                textAlign: 'left',
                                fontWeight: 'normal',
                                color: '#1e293b'
                              }}
                            >
                              {/* Sort items */}
                              <div 
                                className="excel-dropdown-item" 
                                onClick={() => {
                                  setSortConfig({ key: header.key, direction: 'low-to-high' });
                                  setActiveHeaderDropdown(null);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '6px 8px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.74rem',
                                  color: '#334155',
                                  fontWeight: '600'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                <span style={{ color: '#0d47a1', fontSize: '0.8rem' }}>▲</span> Sort Low to High
                              </div>
                              <div 
                                className="excel-dropdown-item" 
                                onClick={() => {
                                  setSortConfig({ key: header.key, direction: 'high-to-low' });
                                  setActiveHeaderDropdown(null);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '6px 8px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.74rem',
                                  color: '#334155',
                                  fontWeight: '600'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                <span style={{ color: '#0d47a1', fontSize: '0.8rem' }}>▼</span> Sort High to Low
                              </div>

                              <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />

                              {/* Filter section */}
                              <div style={{ padding: '2px 8px' }}>
                                <span style={{ fontSize: '0.66rem', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filter</span>
                                {isNumeric ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '5px' }}>
                                    <input 
                                      type="number" 
                                      placeholder="Min value" 
                                      value={rangeFilters[header.key]?.min || ''} 
                                      onChange={e => setRangeFilters({
                                        ...rangeFilters, 
                                        [header.key]: { ...(rangeFilters[header.key] || {}), min: e.target.value }
                                      })}
                                      style={{
                                        width: '100%',
                                        height: '24px',
                                        fontSize: '0.72rem',
                                        padding: '4px 6px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px'
                                      }}
                                    />
                                    <input 
                                      type="number" 
                                      placeholder="Max value" 
                                      value={rangeFilters[header.key]?.max || ''} 
                                      onChange={e => setRangeFilters({
                                        ...rangeFilters, 
                                        [header.key]: { ...(rangeFilters[header.key] || {}), max: e.target.value }
                                      })}
                                      style={{
                                        width: '100%',
                                        height: '24px',
                                        fontSize: '0.72rem',
                                        padding: '4px 6px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div style={{ marginTop: '5px' }}>
                                    <input 
                                      type="text" 
                                      placeholder="Search..." 
                                      value={textFilters[header.key] || ''} 
                                      onChange={e => setTextFilters({
                                        ...textFilters, 
                                        [header.key]: e.target.value
                                      })}
                                      style={{
                                        width: '100%',
                                        height: '24px',
                                        fontSize: '0.72rem',
                                        padding: '4px 6px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </div>
                                )}
                              </div>

                              {/* Clear option */}
                              <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
                              <div 
                                className="excel-dropdown-item" 
                                onClick={() => {
                                  if (isNumeric) {
                                    const next = { ...rangeFilters };
                                    delete next[header.key];
                                    setRangeFilters(next);
                                  } else {
                                    const next = { ...textFilters };
                                    delete next[header.key];
                                    setTextFilters(next);
                                  }
                                  setActiveHeaderDropdown(null);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '6px 8px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.72rem',
                                  color: '#dc2626',
                                  fontWeight: '700'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                ✕ Clear Filter
                              </div>
                            </div>
                          </>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedListings.map((listing) => (
                  <tr key={listing.id}>
                    {getHeaders().map(header => <td key={header.key} className={`${header.key === 'lotNumber' ? 'bold-cell' : ''} ${getColumnClass(header.key)}`}>{renderCell(listing, header.key)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : activeTab === 'mybids' ? (
        <div className="my-bids-section animate-fade-in">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', padding: '8px 12px', background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)', borderRadius: '10px', color: 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.1rem' }}>📋</span>
              <div>
                <div style={{ fontWeight: '800', fontSize: '0.95rem' }}>My Bids & Offers</div>
                <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Track all your active and past bids</div>
              </div>
            </div>
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700' }}>
              {myBids.length} Bid{myBids.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="table-responsive-detailed">
            <table className="premium-table-detailed">
              <thead>
                <tr>
                  <th className="col-primary-info">Bid ID</th>
                  <th className="col-primary-info">Quantity</th>
                  <th className="col-technical-param">Bid Price</th>
                  <th className="col-technical-param">Adjustment</th>
                  <th className="col-primary-info">Date</th>
                  <th className="col-pricing">Status / Seller Response</th>
                </tr>
              </thead>
              <tbody>
                {myBids.length > 0 ? myBids.map(bid => {
                  const statusConfig = {
                    'Pending':   { bg: '#f1f5f9',  color: '#475569',  border: '#cbd5e1', icon: '🕐' },
                    'Accepted':  { bg: '#d1fae5',  color: '#065f46',  border: '#10b981', icon: '✅' },
                    'Rejected':  { bg: '#fee2e2',  color: '#991b1b',  border: '#f87171', icon: '❌' },
                    'Countered': { bg: '#fef3c7',  color: '#92400e',  border: '#fbbf24', icon: '⚡' },
                  };
                  const st = statusConfig[bid.status] || statusConfig['Pending'];
                  return (
                    <tr key={bid.id}>
                      <td className="col-primary-info bold-cell">#{String(bid.id).slice(-6)}</td>
                      <td className="col-primary-info">{bid.quantity} Bales</td>
                      <td className="col-technical-param" style={{ fontWeight: '800', color: '#0d47a1' }}>₹{parseFloat(bid.bidPrice).toLocaleString()}</td>
                      <td className="col-technical-param">{bid.bidDiffSelected || '-'}</td>
                      <td className="col-primary-info">{new Date(bid.timestamp).toLocaleDateString()}</td>
                      <td className="col-pricing">
                        <div className="status-cell-wrapper">
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            padding: '4px 10px', borderRadius: '6px', fontSize: '0.73rem',
                            fontWeight: '700', textTransform: 'uppercase',
                            background: st.bg, color: st.color, border: `1px solid ${st.border}`
                          }}>
                            {st.icon} {bid.status || 'Pending'}
                          </span>
                          {bid.status === 'Countered' && (
                            <div className="buyer-counter-panel">
                              <div className="buyer-counter-details">
                                <div className="counter-title">Seller Counter:</div>
                                <div className="counter-price-item">Product: <strong>₹{parseFloat(bid.counterProductPrice || bid.bidPrice).toLocaleString()}</strong></div>
                                {bid.counterCertPrices && Object.keys(bid.counterCertPrices).length > 0 && (
                                  <div className="counter-certs-list">
                                    {Object.keys(bid.counterCertPrices).map(cId => {
                                      const val = bid.counterCertPrices[cId];
                                      const cert = (bid.includedCerts || []).find(c => c.id === cId);
                                      return (
                                        <div key={cId} className="counter-cert-item">
                                          - {cert ? cert.name : cId}: <strong>₹{parseFloat(val).toLocaleString()}</strong>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              <div className="buyer-counter-actions">
                                <button className="btn-accept-counter" onClick={() => handleBuyerRespondToCounter(bid.id, 'Accepted')}>✅ Accept</button>
                                <button className="btn-reject-counter" onClick={() => handleBuyerRespondToCounter(bid.id, 'Rejected')}>❌ Reject</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                      <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📭</div>
                      <div style={{ fontWeight: '700', fontSize: '0.9rem' }}>No bids placed yet.</div>
                      <div style={{ fontSize: '0.78rem', marginTop: '4px' }}>Go to Spot Bid or Forward Bidding to place your first bid.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="certificates-section animate-fade-in">
          <div className="cert-intro"><h3>Digital Authenticity Store</h3><p>Direct supply of BCI, CMIA and Organic certs.</p></div>
          <div className="cert-grid">
            {['BCI', 'CMIA', 'Npop', 'Nop'].map(cert => (
              <div key={cert} className="cert-card-premium">
                <div className="cert-icon">📑</div><h4>{cert} Certification</h4>
                <div className="cert-price">₹1,500 / Certificate</div>
                <button className="btn-buy-cert" onClick={() => alert(`${cert} purchase flow started`)}>Acquire</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showBidModal && currentListing && (
        <div className="modal-overlay-blur">
          <div className="modal-card-premium">
            <div className="modal-p-header"><h3>Direct Terminal Bid</h3><button className="close-x" onClick={() => setShowBidModal(false)}>×</button></div>
            <div className="modal-p-body">
              <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div className="form-p-group" style={{ flex: '1 1 180px', marginBottom: 0 }}>
                  <label>Quantity Required {currentListing.offerQtyTerms === 'Bales' || currentListing.offerQtyTerms === 'BALS' ? '(Bales)' : '(MT)'}</label>
                  <input
                    type="number"
                    min="0"
                    step={currentListing.offerQtyTerms === 'MT' ? '1' : '50'}
                    placeholder="Enter Qty"
                    value={biddingData.quantity}
                    onChange={e => setBiddingData({ ...biddingData, quantity: e.target.value.replace(/[^0-9]/g, '') })}
                  />
                </div>

                {currentListing.sellingType === 'forward' && currentListing.forwardOffers && currentListing.forwardOffers.length > 0 ? (
                  <div className="form-p-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                    <label>Payment Due Date</label>
                    <select 
                      value={biddingData.paymentDueDate}
                      onChange={e => {
                         const offer = currentListing.forwardOffers.find(o => o.paymentDueDate === e.target.value);
                         setBiddingData({ ...biddingData, paymentDueDate: e.target.value, _forwardOfferPrice: offer ? offer.offerPrice : '' });
                      }}
                    >
                      <option value="">-- Select --</option>
                      {currentListing.forwardOffers.map(o => (
                        <option key={o.paymentDueDate} value={o.paymentDueDate}>
                          {o.paymentDueDate} (Offer: ₹{parseInt(o.offerPrice || 0).toLocaleString()})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : currentListing.paymentDueDate ? (
                  <div className="form-p-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                    <label>
                      Payment Due Date 
                      <span style={{fontSize:'0.65rem', color:'#64748b', display: 'block'}}>
                        (Max Delay: {currentListing.paymentDelayMax || 0} days)
                      </span>
                    </label>
                    <input 
                      type="date" 
                      value={biddingData.paymentDueDate} 
                      onChange={e => setBiddingData({ ...biddingData, paymentDueDate: e.target.value })} 
                    />
                  </div>
                ) : null}

                <div className="form-p-group" style={{ flex: '1 1 180px', marginBottom: 0 }}>
                  <label>Bid Validity</label>
                  <select value={biddingData.validity || ''} onChange={e => setBiddingData({ ...biddingData, validity: e.target.value })}>
                    <option value="">-- Select --</option>
                    <option value="1 hour">1 Hour</option>
                    <option value="4 hours">4 Hours</option>
                    <option value="End of Day">End of Day</option>
                  </select>
                </div>
              </div>

              {biddingData.paymentDueDate && biddingData.paymentDueDate > currentListing.paymentDueDate && (
                <div style={{ color: '#ef4444', fontSize: '0.8rem', marginBottom: '15px', background: '#fef2f2', padding: '6px', borderRadius: '4px' }}>
                  ⚠️ Delaying payment incurs late charges (₹{currentListing.latePaymentCharge || 0}/day).
                  <br />
                  <strong>Calculated Surcharge:</strong> ₹{Math.ceil((new Date(biddingData.paymentDueDate) - new Date(currentListing.paymentDueDate)) / (1000 * 60 * 60 * 24)) * parseFloat(currentListing.latePaymentCharge || 0)} per candy.
                </div>
              )}

              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {/* Certification Bidding Table */}
                <div style={{ flex: '1 1 300px' }}>
                  <table className="premium-table-detailed" style={{ width: '100%', border: '1px solid #000' }}>
                    <thead>
                      <tr>
                        <th colSpan="3" style={{ background: '#fff', color: '#000', border: '1px solid #000', textAlign: 'center', fontSize: '1rem', padding: '8px' }}>Certification - Bidding</th>
                      </tr>
                      <tr>
                        <th style={{ background: '#fff', color: '#000', border: '1px solid #000', padding: '6px' }}>Certificate</th>
                        <th style={{ background: '#fff', color: '#000', border: '1px solid #000', padding: '6px' }}>Offer Price</th>
                        <th style={{ background: '#fff', color: '#000', border: '1px solid #000', padding: '6px' }}>Bid Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentListing.selectedCerts && currentListing.selectedCerts.length > 0 ? (
                        currentListing.selectedCerts.map(cert => {
                          const cb = biddingData.certBids[cert.id] || { checked: false, bidPremium: '' };
                          return (
                            <tr key={cert.id}>
                              <td style={{ border: '1px solid #000', textAlign: 'left', padding: '6px' }}>
                                <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', margin: 0, fontWeight: 'normal' }}>
                                  <input type="checkbox" checked={cb.checked} onChange={() => {
                                      setBiddingData(prev => ({
                                        ...prev,
                                        certBids: {
                                          ...prev.certBids,
                                          [cert.id]: {
                                            checked: !cb.checked,
                                            bidPremium: !cb.checked ? cert.premium : ''
                                          }
                                        }
                                      }));
                                  }} />
                                  {cert.name}
                                </label>
                              </td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'right' }}>{cert.premium}</td>
                              <td style={{ border: '1px solid #000', padding: '0' }}>
                                <input type="text" value={cb.bidPremium} onChange={e => {
                                  if (cb.checked) {
                                    setBiddingData(prev => ({
                                      ...prev,
                                      certBids: { ...prev.certBids, [cert.id]: { ...prev.certBids[cert.id], bidPremium: e.target.value.replace(/[^0-9]/g, '') } }
                                    }));
                                  }
                                }} disabled={!cb.checked} style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', padding: '6px', boxSizing: 'border-box', textAlign: 'right', outline: 'none' }} />
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="3" style={{ border: '1px solid #000', padding: '15px', textAlign: 'center' }}>No certifications</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Cotton Bidding Table */}
                <div style={{ flex: '1 1 300px' }}>
                  <table className="premium-table-detailed" style={{ width: '100%', border: '1px solid #000' }}>
                    <thead>
                      <tr>
                        <th colSpan="3" style={{ background: '#fff', color: '#000', border: 'none', borderBottom: '1px solid #000', textAlign: 'left', fontSize: '1rem', padding: '8px', fontWeight: 'bold' }}>Cotton Bidding</th>
                      </tr>
                      <tr>
                        <th style={{ background: '#fff', color: '#000', border: '1px solid #000', borderTop: 'none', borderLeft: 'none', padding: '6px' }}>Lot No</th>
                        <th style={{ background: '#fff', color: '#000', border: '1px solid #000', borderTop: 'none', padding: '6px' }}>Offer Price</th>
                        <th style={{ background: '#fff', color: '#000', border: '1px solid #000', borderTop: 'none', borderRight: 'none', padding: '6px' }}>Bid Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ border: '1px solid #000', borderLeft: 'none', borderBottom: '1px solid #000', padding: '6px', textAlign: 'right' }}>{currentListing.lotNumber || currentListing.origin || '-'}</td>
                        <td style={{ border: '1px solid #000', borderBottom: '1px solid #000', padding: '6px', textAlign: 'right' }}>
                          {(() => {
                            if (currentListing.sellingType === 'forward' && currentListing.monthlyPrices) {
                              const n = new Date();
                              const MN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                              const mk = `${MN[n.getMonth()]}-${n.getFullYear()}`;
                              const prices = currentListing.monthlyPrices instanceof Map ? Object.fromEntries(currentListing.monthlyPrices) : (currentListing.monthlyPrices || {});
                              const mp = prices[mk];
                              if (mp) return `${parseInt(mp).toLocaleString()}`;
                              const entries = Object.entries(prices).filter(([,v]) => v);
                              if (entries.length) return `${parseInt(entries[0][1]).toLocaleString()}`;
                            }
                            return `${parseInt(biddingData._forwardOfferPrice || currentListing.offerPrice || 0).toLocaleString()}`;
                          })()}
                        </td>
                        <td style={{ border: '1px solid #000', borderRight: 'none', borderBottom: '1px solid #000', padding: '0' }}>
                          <input 
                            type="text" 
                            value={biddingData.customBidPrice} 
                            onChange={e => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              setBiddingData({ ...biddingData, customBidPrice: val, bidDiff: 'other' });
                            }} 
                            style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', padding: '6px', boxSizing: 'border-box', textAlign: 'right', outline: 'none' }} 
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="form-p-group pin-security-group" style={{ marginTop: '20px', maxWidth: '200px' }}>
                <label>Security PIN (4 Digits)</label>
                <input
                  type="password"
                  placeholder="••••"
                  maxLength="4"
                  pattern="\d{4}"
                  inputMode="numeric"
                  value={biddingData.securityPin}
                  onChange={e => setBiddingData({ ...biddingData, securityPin: e.target.value.replace(/[^0-9]/g, '') })}
                  className="buyer-pin-input"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div className="modal-p-footer"><button className="btn-cancel" onClick={() => setShowBidModal(false)}>Discard</button><button className="btn-submit-premium" onClick={submitBid}>Submit Binding Bid</button></div>
          </div>
        </div>
      )}

      {activeRemarks && (
        <div className="modal-overlay-blur" onClick={() => setActiveRemarks(null)}>
          <div className="remarks-popup-card animate-scale-up" onClick={e => e.stopPropagation()}>
            <div className="remarks-popup-header">
              <h4>Lot Remarks</h4>
              <button className="close-x" onClick={() => setActiveRemarks(null)}>×</button>
            </div>
            <div className="remarks-popup-body">
              <p>{activeRemarks}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerDomesticHvi;
